import { AppContext } from '../../../../helpers'
import { Uri } from '../../../../core/models'
import { EntityMappingManager } from '../../'

export class JsonapiUrlGenerator {

  private _searchStrategyRegistry: Map < string, any > = new Map<string, any>()
  private _apiRootUrl: Uri

  public constructor(
    private _apiRoot: string,
    private _endpointMapping: {},
    private _entityMappingManager: EntityMappingManager,
    private _appContext: AppContext
  ) {
    this._searchStrategyRegistry.set('string', this._specificSearchStrategy)
    this._searchStrategyRegistry.set('number', this._specificSearchStrategy)
    this._searchStrategyRegistry.set('object', this._matchingSearchStrategy)
  }

  public generateUrl(type: string, data?: string | {}, includes: string[] = []): string {
    let uri = this._getBaseUrl(type)
    uri = this._runSearchStrategy(uri, type, data)
    uri = this._addIncludesToUri(uri, type, includes)
    return uri.toString()
  }

  private _getBaseUrl(type: string): Uri {
    if (typeof type !== 'string') {
      throw new Error('Cannot determine an endpoint url; class name must be a string')
    }
    const endpointPath: string = this._endpointMapping[type.toLowerCase()]
    if (!endpointPath) {
      throw new Error(`Endpoint for class "${type}" is unknown`)
    }
    const contextualizedEndpointPath = endpointPath.replace(/:user_id/g, this._appContext.currentUser.id)
    const baseUri = new Uri(this._apiRoot)
    if (!contextualizedEndpointPath.startsWith('/')) {
      baseUri.mergeSuffix(contextualizedEndpointPath)
    } else {
      baseUri.setPath(contextualizedEndpointPath)
    }
    return baseUri
  }

  private _runSearchStrategy(uri: Uri, type: string, data: string| {}): Uri {
    const strategy = this._getSearchStrategy(data)
    return strategy.call(this, uri, type, data)
  }

  private _getSearchStrategy(data: string| {}) {
    return this._searchStrategyRegistry.get(typeof data) || this._nullSearchStrategy
  }

  private _specificSearchStrategy(uri: Uri, type: string, id: string) {
    uri.addPathPart(id)
    return uri
  }

  private _matchingSearchStrategy(uri: Uri, type: string, searchCriteria: {}) {
    return this._getFilterQueryStringPart(uri, type, searchCriteria)
  }

  private _nullSearchStrategy(uri: Uri, type: string, data: any) {
    return uri
  }

  /**
   * Assembles a JSON API include query string based on given includes
   *
   * @param uri instance to add the include query string to
   * @param type type of the BaseModel class
   * @param includedPropertiesNames Array of strings or string of attributes to be fetched in the result
   *
   * @return the modified uri
   *
   * @example includedPropertiesNames for a UserTask could for instance look like
   *  ['task'] or ['task.user', 'user-task']
   */
  private _addIncludesToUri(uri: Uri, type: string, includedPropertiesNames: string[]): Uri {
    const includeQueryStringParts: string[] = []
    const transformedProperties = includedPropertiesNames.map((includeSpec) => {
      return this._includeSpecToJsonApiName(type, includeSpec)
    }).join(',')

    if (transformedProperties) {
      uri.addQueryStringPart('include', transformedProperties)
    }

    return uri
  }

  /**
   * Translates a given string of (dot-separated) attribute names on the client side,
   * to a string of (dot-separated) attribute names on the server side, so that JSON API can process
   * this information
   *
   * @param type type of the BaseModel class
   * @param includeSpec a dot-separated list of attributes names provide a relationship chain starting in the named class
   */
  private _includeSpecToJsonApiName(type: string, includeSpec: string): string {
    return includeSpec.split('.').map((propertyName) => {
      return this._entityAttributeToJsonapi(type, propertyName)
    }).join('.')
  }

  private _entityAttributeToJsonapi(type: string, entityAttribute: string) {
    const entityMapping = this._entityMappingManager.forClass(type)
    return entityMapping.getJsonPropertyName(entityAttribute)
  }

  /**
   * Assembles a JSON API filter query string based on given search/filter criteria
   *
   * @param className Name of the BaseModel class
   * @param searchCriteria Hash containing attributes and values of the class, similar to a query by example,
   *        that should be queried and be satisfied by the result + includes that should be fetched
   *
   * @return Query string for JSON API filters based on searchCriteria
   *
   * @example searchCriteria for a UserTask could for instance look like
   * {
   *   flagged: 1
   *   include: ['task']
   * }
   */
  private _getFilterQueryStringPart(
    uri: Uri,
    className: string,
    searchCriteria: { [criterionName: string]: any } = {}
  ): Uri {
    for (const searchCriteriaName in searchCriteria) {
      const translatedName = this._entityAttributeToJsonapi(className, searchCriteriaName)
      const filterValue = this._asFilterValue(searchCriteria[searchCriteriaName])
      if (typeof filterValue !== 'undefined') {
        uri.addQueryStringPart(`filter[${translatedName}]`, filterValue)
      }
    }
    return uri
  }

  /**
   * Converts a given value that is to be used as a filter value to a string that is processable
   * by the JSON API endpoint
   *
   * @param valueToBeUsedAsFilter Any given object that should be used as filter
   *
   * @return Transformed string
   * @throws Exception if value cannot be transformed
   */
  private _asFilterValue(valueToBeUsedAsFilter: any): string {
    let filterValue = valueToBeUsedAsFilter
    // Transform 'objects'
    if (typeof filterValue === 'object' && filterValue != null) {
      if (typeof filterValue['id'] !== 'undefined') {
        filterValue = filterValue['id']
      } else if (Array.isArray(filterValue)) {
        filterValue.join(',')
      } else {
        throw new Error(`Cannot handle the value to be filtered as it is an object: ${filterValue}`)
      }
    }
    return filterValue
  }
}

interface StrategyInfo {
  data?: string | {},
  type?: number
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/model-repository/backends/json-api-backend/jsonapi-url-generator/jsonapi-url.generator.ts